import {
  resolveSalaoProducaoLabels,
  type SalaoProducaoLabels,
} from "../../Painel/utils/origemPainel";
import type { FilaBebidaItemAgrupado } from "./agruparItensFilaBebidas";

export type BebidaMesaTicket = {
  key: string;
  labels: SalaoProducaoLabels;
  createdAt: string;
  /** Menor id da mesa — ordem de chegada / prioridade. */
  sortKey: number;
  linhas: FilaBebidaItemAgrupado[];
  allItemIds: number[];
};

function trimOrNull(value?: string | number | null): string | null {
  if (value == null) return null;
  const t = String(value).trim();
  return t || null;
}

/** Chave de agrupamento: mesa → comanda → pedido. */
export function buildBebidaMesaKey(item: FilaBebidaItemAgrupado): string {
  const mesaId = trimOrNull(item.pedidoFoodfly?.mesaId);
  if (mesaId) return `mesa:${mesaId.toLowerCase()}`;

  const comandaId = trimOrNull(item.pedidoFoodfly?.comandaId);
  if (comandaId) return `comanda:${comandaId.toLowerCase()}`;

  return `pedido:${item.numeroPedido}`;
}

function resolveLabelsForTicket(
  linhas: FilaBebidaItemAgrupado[],
): SalaoProducaoLabels {
  const principal = linhas[0];
  return resolveSalaoProducaoLabels({
    mesaId: principal.pedidoFoodfly?.mesaId,
    comandaId: principal.pedidoFoodfly?.comandaId,
    identificador: principal.pedidoFoodfly?.identificador,
    displayId: principal.numeroPedido,
  });
}

/** Prioridade da linha: menor id = chegou primeiro. */
export function linhaSortKey(linha: FilaBebidaItemAgrupado): number {
  if (!linha.itemIds.length) return Number.MAX_SAFE_INTEGER;
  return Math.min(...linha.itemIds);
}

/**
 * Agrupa linhas de produto já colapsadas em tickets por mesa/comanda/pedido.
 * Ordem: tickets e linhas por chegada (id asc) — itens novos ficam abaixo.
 */
export function agruparBebidasPorMesa(
  items: FilaBebidaItemAgrupado[],
): BebidaMesaTicket[] {
  const map = new Map<string, FilaBebidaItemAgrupado[]>();

  for (const item of items) {
    const key = buildBebidaMesaKey(item);
    const grupo = map.get(key);
    if (grupo) grupo.push(item);
    else map.set(key, [item]);
  }

  const tickets: BebidaMesaTicket[] = [];

  for (const [key, linhas] of Array.from(map.entries())) {
    linhas.sort((a, b) => {
      const byId = linhaSortKey(a) - linhaSortKey(b);
      if (byId !== 0) return byId;
      return (
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });

    const allItemIds = linhas.flatMap((l) => l.itemIds);
    const sortKey = allItemIds.length
      ? Math.min(...allItemIds)
      : Number.MAX_SAFE_INTEGER;
    const createdAt = linhas.reduce((oldest, linha) => {
      return new Date(linha.createdAt).getTime() < new Date(oldest).getTime()
        ? linha.createdAt
        : oldest;
    }, linhas[0].createdAt);

    tickets.push({
      key,
      labels: resolveLabelsForTicket(linhas),
      createdAt,
      sortKey,
      linhas,
      allItemIds,
    });
  }

  tickets.sort((a, b) => {
    if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
    return (
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  });

  return tickets;
}
